import { all, call, delay, fork, join, put, race, take, takeEvery, select } from "redux-saga/effects";

import * as UploadAction from "../actions/uploadAction";
import * as JobAction from "../actions/jobAction";
import * as UserAction from "../actions/userAction";
import * as api from "../../api";
import { showErrorMessage } from "./shared";
import { IUploadDoubleSidedJobImageRequest, IUploadImageRequest, IUploadJobImageRequest, IUploadJobInfoImageRequest, IUploadLogoImageRequest as IUploadLogoImageRequest } from "../../models/UploadRequest";
import { getFileExtensionFromDataUri, getFileExtensionFromFileName } from "src/utils/utils";
import { JobStatus } from "../../models";
import { isElectron, shouldUseLocalProcessing } from "../../utils/miscUtils";
import { IStore } from "../reducers";

export const getNetworkStatus = (store: IStore) => store.app.isOnline;
export const getPreferredProcessingMode = (store: IStore) => store.settings.preferredProcessingMode;
export const getCommittedDraftInfoByJobId = (store: IStore) => store.job.committedDraftInfoByJobId;
export const getCenter = (store: IStore) => store.user.userProfile.response?.center;
export const getGrainType = (store: IStore) => store.grainType.grainId;
export const getProfileId = (store: IStore) => store.grainProfile.profileId;
export const getUsername = (store: IStore) => store.user.userProfile.response?.username;
const CREATE_JOB_WAIT_TIMEOUT_MS = 30000;

const getBrowserOnline = (): boolean | undefined => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
      return navigator.onLine;
    }
  } catch {
    // ignore
  }
  return undefined;
};

const blobUrlToDataUri = async (blobUrl: string): Promise<string> => {
  if (typeof fetch !== 'function') {
    throw new Error('Cannot resolve blob: URL (fetch unavailable)');
  }
  if (typeof FileReader === 'undefined') {
    throw new Error('Cannot resolve blob: URL (FileReader unavailable)');
  }

  const response = await fetch(blobUrl);
  const blob = await response.blob();

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read blob data'));
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to convert blob to data URI'));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(blob);
  });
};

const resolveElectronLocalImageSpecifier = async (value?: string): Promise<string | undefined> => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }
  if (value.startsWith('blob:')) {
    return await blobUrlToDataUri(value);
  }
  return value;
};

const toFileUri = (value?: string): string | undefined => {
  if (!value || typeof value !== 'string') return undefined;
  if (/^(blob:|data:|https?:|file:)/i.test(value)) return value;

  const normalized = value.replace(/\\/g, '/');
  if (/^[A-Za-z]:\//.test(normalized)) {
    return `file:///${encodeURI(normalized)}`;
  }
  if (normalized.startsWith('/')) {
    return `file://${encodeURI(normalized)}`;
  }
  return value;
};

const normalizeCloudUploadAsset = (asset: any): any => {
  if (asset && typeof asset === 'object' && typeof asset.uri === 'string') {
    return { ...asset, uri: toFileUri(asset.uri) || asset.uri };
  }
  if (typeof asset === 'string') {
    return { uri: toFileUri(asset) || asset };
  }
  return asset;
};

const getCloudUploadFileExtension = (asset: any, isBase64: boolean): string => {
  if (isBase64) return getFileExtensionFromDataUri(asset);

  if (asset && typeof asset === 'object' && typeof asset.uri === 'string') {
    return getFileExtensionFromFileName(asset.uri);
  }
  if (typeof asset === 'string') {
    return getFileExtensionFromFileName(asset);
  }
  return 'jpg';
};

function* requestCloudJobAndWait(createPayload: any): any {
  // Start listening before dispatching request so fast failures/successes are not missed.
  const waiter = yield fork(function* waitCreateJobOutcome() {
    return yield race({
      success: take(JobAction.createJob.success),
      failure: take(JobAction.createJob.failure),
      timeout: delay(CREATE_JOB_WAIT_TIMEOUT_MS)
    });
  });

  yield put(JobAction.createJob.request(createPayload));
  const outcome = yield join(waiter);

  if (outcome?.timeout) {
    throw new Error('Timed out while creating cloud job. Please retry.');
  }
  if (outcome?.failure) {
    const err = outcome.failure.payload;
    throw new Error(err?.message || 'Failed to create cloud job');
  }
}

function* upsertOptimisticCloudJobStatus(args: {
  jobId: string;
  status: JobStatus;
  source?: string;
  location?: string;
  productName?: string;
}): any {
  const grainId = yield select(getGrainType);
  const profileId = yield select(getProfileId);
  const username = yield select(getUsername);
  const committedDraftInfoByJobId = yield select(getCommittedDraftInfoByJobId);
  const committedInfo = committedDraftInfoByJobId?.[args.jobId] || {};
  const nowEpochSeconds = Math.floor(Date.now() / 1000);

  yield put(JobAction.upsertOptimisticUserJob({
    jobId: args.jobId,
    grainId,
    profileId,
    jobStatus: args.status,
    processingMode: 'cloud',
    createdAt: nowEpochSeconds,
    updatedAt: nowEpochSeconds,
    username,
    source: args.source,
    location: args.location,
    info: {
      ...committedInfo,
      location: args.location
    },
    productName: args.productName
  }));
}

export function* upload(action: ReturnType<typeof UploadAction.upload.request>): any {
  const request: IUploadJobImageRequest = action.payload;
  try {
    const isOnline: boolean = yield select(getNetworkStatus);
    const preferredProcessingMode: 'local' | 'cloud' | undefined = yield select(getPreferredProcessingMode);
    const browserOnline = getBrowserOnline();
    const effectiveOnline = isOnline && (!isElectron() || browserOnline !== false);

    const object_key = request.isBase64
      ? `input/${request.jobId}.jpg`
      : `input/${request.jobId}.${getCloudUploadFileExtension(request.dataObject, false)}`;

    const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);

    if (useLocalProcessing && isElectron()) {
      const committedDraftInfoByJobId = yield select(getCommittedDraftInfoByJobId);
      const committedInfo = committedDraftInfoByJobId?.[request.jobId] || {};
      const center = yield select(getCenter);
      const info: any = {
        ...committedInfo,
        location: request.location
      };
      if (center?.centerId) info.centerId = String(center.centerId);
      if (center?.name) info.centerName = String(center.name);
      if (center?.addressDistrict) info.centerDistrict = String(center.addressDistrict);
      if (center?.addressCity) info.centerCity = String(center.addressCity);

      yield put(JobAction.updateRpiJobStatus({
        jobId: request.jobId,
        status: JobStatus.JOB_PROCESSING_LOCAL,
      }));

      let imagePath = undefined;
      const dataObject: any = request.dataObject;
      if (dataObject && typeof dataObject === 'object' && typeof dataObject.uri === 'string') {
        imagePath = yield call(resolveElectronLocalImageSpecifier, dataObject.uri);
      }

      yield put(JobAction.processLocalJob.request({
        jobId: request.jobId,
        source: request.source,
        location: request.location,
        info,
        imagePath
      }));

      // Mark upload step as completed for UI flows (local mode does not upload to cloud).
      yield put(UploadAction.upload.success(object_key));
      yield put(JobAction.clearCommittedDraftInfo(request.jobId));
      return;
    }

    if (!effectiveOnline) {
      yield put(JobAction.updateRpiJobStatus({
        jobId: request.jobId,
        status: JobStatus.JOB_FAILED,
      }));
      throw new Error('Internet connection required in Cloud mode. Switch to Local mode to process offline.');
    }

    yield call(upsertOptimisticCloudJobStatus, {
      jobId: request.jobId,
      status: JobStatus.JOB_IMAGE_UPLOADING,
      source: request.source,
      location: request.location
    });

    yield call(requestCloudJobAndWait, {
      jobId: request.jobId,
      test: false,
      object_key: object_key,
      source: request.source,
      location: request.location,
    });

    const normalizedRequest: IUploadJobImageRequest = {
      ...request,
      dataObject: normalizeCloudUploadAsset(request.dataObject)
    };
    yield call(uploadToCloud, normalizedRequest, object_key);
    yield call(upsertOptimisticCloudJobStatus, {
      jobId: request.jobId,
      status: JobStatus.JOB_IMAGE_UPLOADED,
      source: request.source,
      location: request.location
    });
  } catch (error: any) {
    showErrorMessage(error);
    if (request?.jobId) {
      yield put(JobAction.updateRpiJobStatus({
        jobId: request.jobId,
        status: JobStatus.JOB_FAILED,
      }));
      yield call(upsertOptimisticCloudJobStatus, {
        jobId: request.jobId,
        status: JobStatus.JOB_FAILED,
        source: request.source,
        location: request.location
      });
    }
    yield put(UploadAction.upload.failure(error));
  }
}

function* uploadToCloud(request: IUploadJobImageRequest, object_key: string) {
  const uploadRequest: IUploadImageRequest = {
    object_key: object_key,
    isBase64: request.isBase64,
    dataObject: request.dataObject
  };

  const uploadImageResp: boolean | null = yield call(api.uploadImage, uploadRequest);
  if (uploadImageResp !== true) {
    throw new Error('Image upload did not complete.');
  }

  yield put(UploadAction.upload.success(object_key));
  yield put(JobAction.pollJobStatus.request(request.jobId));

  yield put(JobAction.updateRpiJobStatus({
    jobId: request.jobId,
    status: JobStatus.JOB_IMAGE_UPLOADED,
  }));

  return true;
}

export function* uploadDoubleSided(action: ReturnType<typeof UploadAction.uploadDoubleSided.request>): any {
  const request: IUploadDoubleSidedJobImageRequest = action.payload;
  try {
    const isOnline: boolean = yield select(getNetworkStatus);
    const preferredProcessingMode: 'local' | 'cloud' | undefined = yield select(getPreferredProcessingMode);
    const browserOnline = getBrowserOnline();
    const effectiveOnline = isOnline && (!isElectron() || browserOnline !== false);

    const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);
    if (useLocalProcessing && isElectron()) {
      const committedDraftInfoByJobId = yield select(getCommittedDraftInfoByJobId);
      const committedInfo = committedDraftInfoByJobId?.[request.jobId] || {};
      const center = yield select(getCenter);
      const info: any = {
        ...committedInfo,
        location: request.location
      };
      if (center?.centerId) info.centerId = String(center.centerId);
      if (center?.name) info.centerName = String(center.name);
      if (center?.addressDistrict) info.centerDistrict = String(center.addressDistrict);
      if (center?.addressCity) info.centerCity = String(center.addressCity);

      yield put(JobAction.updateRpiJobStatus({
        jobId: request.jobId,
        status: JobStatus.JOB_PROCESSING_LOCAL,
      }));

      const imagePaths: string[] = (request.dataObject || [])
        .map((item: any) => {
          if (item && typeof item === 'object' && typeof item.uri === 'string') return item.uri;
          if (typeof item === 'string') return item;
          return undefined;
        })
        .filter((p: any) => typeof p === 'string');

      const resolvedImagePaths: string[] = [];
      for (const p of imagePaths) {
        const resolved = yield call(resolveElectronLocalImageSpecifier, p);
        if (resolved) {
          resolvedImagePaths.push(resolved);
        }
      }

      yield put(JobAction.processLocalJob.request({
        jobId: request.jobId,
        source: request.source,
        location: request.location,
        info,
        imagePaths: resolvedImagePaths,
        productName: 'DOUBLE_SIDED_GRAIN_ANALYSIS'
      }));

      yield put(UploadAction.uploadDoubleSided.success(`input/${request.jobId}/`));
      yield put(JobAction.clearCommittedDraftInfo(request.jobId));
      return;
    }

    if (!effectiveOnline) {
      yield put(JobAction.updateRpiJobStatus({
        jobId: request.jobId,
        status: JobStatus.JOB_FAILED,
      }));
      throw new Error('Internet connection required in Cloud mode. Switch to Local mode to process offline.');
    }

    yield call(upsertOptimisticCloudJobStatus, {
      jobId: request.jobId,
      status: JobStatus.JOB_IMAGE_UPLOADING,
      source: request.source,
      location: request.location,
      productName: 'DOUBLE_SIDED_GRAIN_ANALYSIS'
    });

    if (request.dataObject === undefined || request.dataObject === null
      || request.dataObject.length !== 2) {
      throw Error("Invalid data object");
    }

    const object_dir_key = `input/${request.jobId}/`;

    yield call(requestCloudJobAndWait, {
      jobId: request.jobId,
      test: false,
      object_key: object_dir_key,
      source: request.source,
      location: request.location,
    });

    const object_keys = [];

    for (let i = 0; i < request.dataObject.length; i++) {
      if (request.dataObject[i] === undefined || request.dataObject[i] === null) {
        throw Error("Invalid data object");
      }

      const normalizedAsset = normalizeCloudUploadAsset(request.dataObject[i]);
      const fileExtension = getCloudUploadFileExtension(normalizedAsset, !!request.isBase64);

      const device_key = `device_${i + 1}`;

      const object_key = `input/${request.jobId}/${device_key}.${fileExtension}`

      const uploadRequest: IUploadImageRequest = {
        object_key: object_key,
        isBase64: request.isBase64,
        dataObject: normalizedAsset
      };

      const uploadImageResp: boolean | null = yield call(api.uploadImage, uploadRequest);
      if (uploadImageResp && uploadImageResp === true) {
        object_keys.push(object_key);
      } else {
        throw Error("Error uploading image");
      }
    }

    yield put(UploadAction.uploadDoubleSided.success(object_dir_key));
    yield put(JobAction.pollJobStatus.request(request.jobId));

    // Update the job status to trigger UI refresh
    yield put(JobAction.updateRpiJobStatus({
      jobId: request.jobId,
      status: JobStatus.JOB_IMAGE_UPLOADED,
    }));
    yield call(upsertOptimisticCloudJobStatus, {
      jobId: request.jobId,
      status: JobStatus.JOB_IMAGE_UPLOADED,
      source: request.source,
      location: request.location,
      productName: 'DOUBLE_SIDED_GRAIN_ANALYSIS'
    });
  } catch (error: any) {
    showErrorMessage(error);
    if (request?.jobId) {
      yield put(JobAction.updateRpiJobStatus({
        jobId: request.jobId,
        status: JobStatus.JOB_FAILED,
      }));
      yield call(upsertOptimisticCloudJobStatus, {
        jobId: request.jobId,
        status: JobStatus.JOB_FAILED,
        source: request.source,
        location: request.location,
        productName: 'DOUBLE_SIDED_GRAIN_ANALYSIS'
      });
    }
    yield put(UploadAction.uploadDoubleSided.failure(error));
  }
}

export function* uploadLogo(action: ReturnType<typeof UploadAction.uploadLogo.request>): any {
  try {
    const request: IUploadLogoImageRequest = action.payload;

    const fileExtension = request.isBase64 ? getFileExtensionFromDataUri(request.dataObject) : `png`;
    const objectKey = request.type === "org" ? `profile/org/${request.orgId}.${fileExtension}` : `profile/user/${request.username}.${fileExtension}`;

    const uploadRequest: IUploadImageRequest = {
      object_key: objectKey,
      isBase64: request.isBase64,
      dataObject: request.dataObject
    };

    const uploadImageResp: boolean | null = yield call(api.uploadImage, uploadRequest);
    if (uploadImageResp
      && uploadImageResp === true
      && request.username !== undefined) {
      yield put(UserAction.updateUserLogo.request({
        username: request.username,
        logo: objectKey
      }));

      yield put(UploadAction.uploadLogo.success(objectKey));
    }
  } catch (error: any) {
    showErrorMessage(error);
    yield put(UploadAction.uploadLogo.failure(error));
  }
}

export function* uploadJobInfoImage(action: ReturnType<typeof UploadAction.uploadJobInfoImage.request>): any {
  try {
    const request: IUploadJobInfoImageRequest = action.payload;

    const fileExtension = request.isBase64 ? getFileExtensionFromDataUri(request.dataObject) : `png`;
    
    const objectKey = `output/${request.jobId}/job_info/${request.fieldName}.${fileExtension}`;
    
    const uploadRequest: IUploadImageRequest = {
      object_key: objectKey,
      isBase64: request.isBase64,
      dataObject: request.dataObject
    };

    const uploadImageResp: boolean | null = yield call(api.uploadImage, uploadRequest);
    if (uploadImageResp
      && uploadImageResp === true) {
      
      yield put(UploadAction.uploadJobInfoImage.success({
        jobId: request.jobId,
        fieldName: request.fieldName,
        s3Path: objectKey
      }));
    }
  } catch (error: any) {
    showErrorMessage(error);
    yield put(UploadAction.uploadJobInfoImage.failure({
      jobId: action.payload.jobId,
      fieldName: action.payload.fieldName,
      error: error
    }));
  }
}

/*
 * WATCHERS
 */

export function* watchUpload() {
  yield takeEvery(UploadAction.upload.request, upload);
}

export function* watchUploadDoubleSided() {
  yield takeEvery(UploadAction.uploadDoubleSided.request, uploadDoubleSided);
}

export function* watchUploadLogo() {
  yield takeEvery(UploadAction.uploadLogo.request, uploadLogo);
}

export function* watchUploadJobInfoImage() {
  yield takeEvery(UploadAction.uploadJobInfoImage.request, uploadJobInfoImage);
}

export default function* root() {
  yield all([fork(watchUpload),
  fork(watchUploadDoubleSided),
  fork(watchUploadJobInfoImage),
  fork(watchUploadLogo)]);
}
